import { DataType } from "@/components/DataTable/Models";
import { IconTable } from "@codexteam/icons";
import { API, BlockAPI, BlockTool } from "@editorjs/editorjs";
import { BlockToolConstructorOptions, BlockToolData } from "@editorjs/editorjs/types/tools";
import { createRoot } from "react-dom/client";
import { ClassificationTableBlockComponent } from "./ClassificationTableBlockComponent";

export interface ClassificationTableBlockData extends BlockToolData {
    title?: string;
    caption?: string;
    columns: { value: string; dataType: DataType }[];
}

export interface ClassificationTableBlockConfig {
    currentLanguage: string;
}

const DEFAULT_CONFIG: { [key: string]: ClassificationTableBlockData } = {
    "int-en": {
        title: "",
        columns: [
            {
                value: "Classification",
                dataType: DataType.STRING,
            },
            {
                value: "Number - Description",
                dataType: DataType.STRING,
            },
        ],
    },

    "int-fr": {
        title: "",
        columns: [
            {
                value: "Classification",
                dataType: DataType.STRING,
            },
            {
                value: "Numéro - Descriptif",
                dataType: DataType.STRING,
            },
        ],
    },

    "int-de": {
        title: "",
        columns: [
            {
                value: "Classification",
                dataType: DataType.STRING,
            },
            {
                value: "Number - Description",
                dataType: DataType.STRING,
            },
        ],
    },

    "int-nl": {
        title: "",
        columns: [
            {
                value: "Classificatie",
                dataType: DataType.STRING,
            },
            {
                value: "Nummer - Beschrijving",
                dataType: DataType.STRING,
            },
        ],
    },
};

export class ClassificationTableBlockTool implements BlockTool {
    public static get toolbox() {
        return {
            title: "Classifications",
            icon: IconTable,
        };
    }

    api: API;
    block?: BlockAPI;
    config: ClassificationTableBlockData;
    data: ClassificationTableBlockData;
    currentData: ClassificationTableBlockData;
    CSS: { [key: string]: string };
    nodes: { [key: string]: HTMLElement | null };

    // Use `any` to circumvent an issue with Editor.js when using TypeScript
    constructor(params: BlockToolConstructorOptions<ClassificationTableBlockData, ClassificationTableBlockConfig>) {
        const { api, block, config, data } = params;

        if (!config) throw new Error("ClassificationTableBlockTool configuration missing");

        this.api = api;
        this.block = block;
        this.data = data;
        this.config = DEFAULT_CONFIG[config.currentLanguage]!;

        if (data && Object.keys(data).length > 0) {
            this.data = data;
        } else {
            this.data = JSON.parse(JSON.stringify(DEFAULT_CONFIG[config.currentLanguage]!));
        }

        this.currentData = JSON.parse(JSON.stringify(this.data));

        this.CSS = {
            wrapper: "template-editor-content-block",
        };

        this.nodes = {
            holder: null,
            table: null,
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    save(block: HTMLElement) {
        return {
            title: this.api.sanitizer.clean(this.currentData.title || "", {}),
            columns: this.currentData.columns.map((column) => ({
                ...column,
                value: this.api.sanitizer.clean(column.value || "", {}),
            })),
            caption: this.api.sanitizer.clean(this.currentData.caption || "", {}),
        };
    }

    render(): HTMLElement {
        if (!this.nodes.table) {
            const rootNode = document.createElement("div");
            rootNode.setAttribute("class", this.CSS.wrapper);
            this.nodes.table = rootNode;

            const root = createRoot(rootNode);
            root.render(
                <ClassificationTableBlockComponent
                    data={this.data}
                    defaults={this.config}
                    onChange={(data) => {
                        this.currentData = data;
                        this.block?.dispatchChange();
                    }}
                />
            );
        }

        if (!this.nodes.holder) {
            const holderNode = document.createElement("div");
            holderNode.setAttribute("class", this.CSS.wrapper);
            holderNode.appendChild(this.nodes.table);
            this.nodes.holder = holderNode;
        }

        return this.nodes.holder;
    }
}
